<template>
  <div
    :class="[
      labelPositionClass,
      inputWrapperClass,
      { 'input-has-error': field.hasError }]"
    class="form-control"
    :id="field.id + '-wrapper'"
    v-if="!field.hidden"
  >
    <v-popover v-if="field.showTooltip" trigger="hover">
      &nbsp;<i class="fas fa-info-circle"></i>
      <div slot="popover">{{ field.tooltipText }}</div>
    </v-popover>
    
    <div :class="[field.labelWrapperClass || 'd-flex justify-content-between align-items-baseline']">
      <label v-if="!field.hideLabel" :class="[customLabelClass, { 'muted': field.disabled, 'cursor-pointer': field.labelClickable }]" style="display:inline-block" :for="field.labelClickable ? field.id : ''">
        <p class="m-0 d-inline-flex"><span class="me-1">{{fieldLabel}}</span><span v-if="field.required" class="required text-danger">*</span></p><i v-if="fieldLabelTooltip" v-tooltip="fieldLabelTooltip" class="fas fa-info-circle" tabindex="0" :aria-label="fieldLabelTooltip"></i>
      </label>
      <span v-if="field.hint" class="mx-1 fs-6">{{ field.hint }}</span>
    </div>
    <app-input-text v-if="field.type === FIELD_TYPE.INPUT"
                    v-bind="propsFor('app-input-text', field)"
                    :onChange="(value) => { onChange(value, field) }"
                    class="input-ui app-input"
                    :value="model[field.name]"
                    :disabled="field.disabled"
                    :readOnly="field.readOnly"
                    :onBlurOnly="onBlurOnly"
                    :useDebounce="useDebounce"
                    :onBlur="(e) => { onBlur(e, field) }"
    >
      <template v-if="field.showInfoTooltip && field.infoTooltipPosition === 'afterField'" slot="afterField">
        <v-popover trigger="hover" class="mx-2">
          &nbsp;<i :class="['fas', `fa-${field.infoTooltipIconName || 'info-circle'}`]"></i>
          <div slot="popover">{{ field.infoTooltipText }}</div>
        </v-popover>
      </template>
    </app-input-text>

    <textarea v-if="field.type===FIELD_TYPE.TEXTAREA"
              :id="field.id"
              :name="field.name"
              :placeholder="field.placeholder"
              :class="['input-ui', 'app-input', field.inputClass]"
              :value="model[field.name]"
              :readonly="field.readOnly"
              @input="(e) => { onInputChange(e, field) }"
              @blur="(e) => { onBlur(e.target.value, field) }"
    />
  
    <app-date-picker v-else-if="field.type == FIELD_TYPE.CALENDAR"
                     v-bind="propsFor('app-date-picker', field)"
                     :id="field.id"
                     :value="selectedDate"
                     :field.sync="field"
                     @onInputChange="onChange"
                     class="app-input"
    />

    <div  v-else-if="field.type === 'daymonth'" class="d-flex">
      <SelectDayMonthRange />
    </div>

    <span v-else-if="field.type === FIELD_TYPE.DATE_RANGE" class="modal-field d-flex justify-content-start align-items-start">
      <app-time-periods
        v-bind="propsFor('app-time-periods', field)"
        :queryPeriod="field.value || {}"
        :periods="periodsData"
        :period_types="period_types"
        @setQueryTimePeriod="(value) => onChange(value, field)"
        :showPeriodType="false"
        :periodKey="field.name"
        :showPeriodTypeMessage="false"
      />
      <button type="reset" class="btn btn-sm btn-secondary mx-2" :style="{ width: '2.5rem', height: '2.5rem' }" @click="() => removeCustomCompareDateRange(field)" :disabled="!(field.value && field.value.custom_compare_date_range)"><i class="fa fa-times text-danger" aria-hidden="true"></i></button>
    </span>

    <app-input-select v-else-if="field.type == FIELD_TYPE.SELECT"
                      v-bind="propsFor('app-input-select', field)"
                      :id="field.id"
                      v-model="selectedValue"
                      :allow-empty="field.allowEmpty"
                      :searchable="field.searchable"
                      :searchable-props="field.searchableProps"
                      :disabled="field.disabled"
                      :loading="field.loading"
                      :options-footer-loading="field.optionsFooterLoading"
                      :show-labels=false
                      :preselectFirst="field.preselectFirst"
                      :selectAllTooltipMessage="field.selectAllTooltipMessage"
                      :clearAllTooltipMessage="field.clearAllTooltipMessage"
                      :onChange="(value) => { onChange(value, field) }"
                      :clearSelectedValue="(value) => { clearSelectValue(value, field)}"
                      @onSearchChangeInput="(value) => { onSearchChangeInput(value, field)}"
                      :selectAllAvailableOptions="(value) => { selectAllOptions(value, field) }"
                      v-on="field.events || {}"
                      class="app-input">
      <template v-if="field.translateNoOptions" slot="noOptions">
        <span>{{$t(field.translateNoOptions)}}</span>
      </template>
      <template slot="importexport">
        <slot name="importexport"></slot>
      </template>
    </app-input-select>

    <app-input-checkbox v-else-if="field.type == FIELD_TYPE.CHECKBOX"
                        v-bind="propsFor('app-input-checkbox', field)"
                        :onChange="(value) => { onChange(value, field) }"
                        :value="model[field.name]"
                        :id="field.id"
                        class="app-input" />

    <app-input-toggle v-else-if="field.type == FIELD_TYPE.TOGGLE"
                      v-bind="propsFor('app-input-toggle', field)"
                      :onChange="(value) => { onChange(value, field) }"
                      :value="model[field.name]"
                      :checked="model[field.name]"
                      :id="field.id"
                      class="app-input" />

    <app-input-button v-else-if="field.type == FIELD_TYPE.BUTTON"
                      v-bind="propsFor('app-input-button', field)"
                      :id="field.id"
                      class="app-input" />

    <app-color-picker v-else-if="field.type == FIELD_TYPE.COLOR"
                      v-bind="field"
                      :id="field.id"
                      :onChange="(value) => { onChange(value, field) }"
                      :value="model[field.name]"
                      class="app-input" />

    <app-input-select v-else-if="field.type == FIELD_TYPE.DAYOFWEEK"
                      v-bind="propsFor('app-input-select', field)"
                      :id="field.id"
                      v-model="selectedValue"
                      :options="DAYS_OF_WEEK"
                      :allow-empty="field.allowEmpty"
                      :searchable="field.searchable"
                      :disabled="field.disabled"
                      :show-labels=false
                      :preselectFirst="field.preselectFirst"
                      trackBy="value"
                      :onChange="(value) => { onChange(value, field) }"
                      @onSearchChangeInput="(value) => { onSearchChangeInput(value, field)}"
                      class="app-input"
    />

    <app-input-file v-else-if="field.type == FIELD_TYPE.FILES"
                    v-bind="propsFor('app-input-file', field)"
                    :value="model[field.name]"
                    :onBlur="(e) => { onBlur(e, field) }"
                    :max_number_files="field.max_number_files"
                    @onChange="(value) => { onChange(value, field) }"
                    @uploadComplete="uploadComplete"

    >
      <template v-for="(_, slot) in $slots">
        <template :slot="slot">
          <slot :name="slot"></slot>
        </template>
      </template>
    </app-input-file>

    <FileUpload
      v-else-if="field.type == FIELD_TYPE.FILES_UPLOAD"
      v-bind="field"
      :show_thumbnails_url="field.show_thumbnails_url"
      :removeImageFromServer="field.removeImageFromServer"
      :name="field.name"
      :value="model[field.name]"
      :translatePlaceholder="field.translatePlaceholder"
      :accept="field.accept"
      :max="field.max"
      @onChange="(value) => { onChange(value, field) }"
    />

    <app-query-editor v-else-if="field.type == FIELD_TYPE.QUERYEDITOR"
                      v-bind="propsFor('app-query-editor', field)"
                      :id="field.id"
                      :model="model"
                      @onChange="(value) => { onChange(value, field) }"
                      @onBlur="(e) => { onBlur(e.target.value, field) }"
                      @updateQuerySyntaxValid="updateQuerySyntaxValid" />

    <app-input-boolean-search v-else-if="field.type == FIELD_TYPE.BOOLEANSEARCH"
                    v-bind="propsFor('app-input-boolean-search', field)"
                    :id="field.id"
                    :model="model"
                    :value="model[field.name]"
                    @onChange="(value) => { onChange(value, field) }" />

    <span v-if="field.helperText" class="text-sm fst-italic">{{ field.helperText }}</span>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import _ from 'lodash';
import moment from 'moment';

import { FIELD_TYPE, VALIDATIONS, DAYS_OF_WEEK } from '../../config/const';
import { propsForMixin } from '../../helpers/application';
import { checkDecimalPoints, setDecimalPoints, toFixed } from "../../helpers/numbers/decimalPoints";

import InputButton from './input-button';
import SelectDayMonthRange from './select-day-month/select-day-month-range';
import InputSelect from './input-select';
import InputCheckbox from './input-checkbox';
import InputText from './input-text';
import InputToggle from './input-toggle';
import InputFile from './input-file';
import FileUpload from './file-upload.vue';
import Datepicker from '../date-picker-2';
import ColorPicker from '../color-picker';
import QueryEditor from '../boolean-search/query-builder/query-builder';
import InputBooleanSearch from './input-boolean-search';
import TimePeriods from '@/components/coding-queries/time_periods';

export default {
  props: {
    field: {
      type: Object,
      required: true
    },
    model: {
      type: Object,
      required: true
    },
    onBlurOnly: {
      type: Boolean,
      required: false,
      default: false
    },
    useDebounce: {
      type: Boolean,
      default: true
    },
  },
  components: {
    'app-input-select': InputSelect,
    'app-input-checkbox': InputCheckbox,
    'app-input-text': InputText,
    'app-color-picker': ColorPicker,
    'app-date-picker': Datepicker,
    'app-input-button': InputButton,
    'app-input-toggle': InputToggle,
    'app-input-file': InputFile,
    'app-query-editor': QueryEditor,
    'app-input-boolean-search': InputBooleanSearch,
    'app-time-periods': TimePeriods,
    SelectDayMonthRange,
    FileUpload,
  },
  created() {
    if(this.field && this.field?.name === 'custom_compare_date_range' && !this.field?.value?.custom_compare_date_range) this.field.value = this.periodDateRangesDefaults;
  },
  computed: {
    ...mapState({
        periods: state => state.queryBuilder.periods.periods,
    }),
    periodsData() { return this.periods && this.periods.data && this.periods.data.periods },
    period_types() { return this.periods.data && this.periods.data.types },
    periodDateRangesDefaults() {
      return {type: 'current', [this.field.name]: [moment().subtract(7, 'days').format(this.initDateFormat), moment().format(this.initDateFormat)]};
    },
    selectedValue: {
      get () {
        const model = this.model;
        const field = this.field;
        let InputValue = this.model[field.name];

        if (field.multiple && InputValue === null) InputValue = [];

        if (!_.isUndefined(InputValue) && _.isArray(InputValue) && InputValue.length > 0 && _.isObject(InputValue[0])) {
          InputValue = model[field.name].map(value => field.trackBy && value[field.trackBy] ? value[field.trackBy] : value);
        }
        return InputValue;
      },
      set (newValue) {
        const newModel = { ...this.model };
        newModel[this.field.name] = newValue;
        this.$emit('updateModel', { model: newModel, field: this.field});
      }
    },

    fieldLabel () {
      if (!this.field) return;

      if (this.field.label && typeof this.field.label === 'function') return this.field.label();

      return this.field.translate_label ? this.$i18n.t(this.field.translate_label) : this.field.label
    },
    customLabelClass () {
      if (!this.field) return;
      if (this.field.customLabelClass){
          return this.field.customLabelClass;
      }
      return 'text-gray';
    },

    fieldLabelTooltip () {
      return this.field && this.field.translate_label_tooltip && this.$i18n.te(this.field.translate_label_tooltip) ? this.$i18n.t(this.field.translate_label_tooltip) : false
    },

    selectedDate () {
      const model = this.model;
      const field = this.field;
      const value = model[field.name];

      return value || '';
    },

    labelPositionClass() {
      switch(this.field.labelPosition) {
        case 'left':
          return 'label-left'
        case 'right':
          return 'label-right'
        default:
          return ''
      }
    },
    inputWrapperClass() {
      return this.field.inputWrapperClass || ''
    },
  },
  methods: {
    onInputChange(e, field) {
      const value = e.target.value;
      this.onChange(value, field);
    },
    onChange(value, field = this.field) {
      const newValue = _.cloneDeep(this.model);

      if([FIELD_TYPE.DATE_RANGE]?.indexOf(field?.type) > -1) {
        // update field value
        field.value[field.name] = value?.value;
        // update the new model obj value
        newValue[field.name][value?.entity?.[0]] = value?.value;
      }
      else {
        newValue[field.name] = value;
      }

      if(!this.model || this.model !== newValue) {
        this.callUpdate({ model: newValue, field});
      }

      if(([FIELD_TYPE.CALENDAR, FIELD_TYPE.SELECT, FIELD_TYPE.CHECKBOX, FIELD_TYPE.COLOR].indexOf(field.type) !== -1)) {
        this.onBlur(value, field);
      }

      if((FIELD_TYPE.INPUT === field.type && field.inputType === 'number')) {
        const dp = this.field.decimalPoints || 0;
        if(!dp && value !== 0) newValue[field.name] = null
        this.$nextTick(() => {
          newValue[field.name] = setDecimalPoints(value, dp )
        })
        
        this.onBlur(value, field);
      }
      if ('onUpdate' in field) field.onUpdate(value);
    },
    clearSelectValue(value, field = this.field) {
      if ('clearSelectValue' in field) field.clearSelectValue(value);
    },
    onSearchChangeInput(value, field = this.field) {
      if ('onSearchChangeInput' in field) field.onSearchChangeInput(value);
      this.$emit('onSearchChangeInput', value, field);
    },
    selectAllOptions (value, field = this.field){
      if ('selectAllAvailableOptions' in field) field.selectAllAvailableOptions(value);
    },
    callUpdate({ model, field }) {
      if (this.field.update) this.$emit('updateField', { model, field })

      if (!this.field.blockGeneralUpdate) this.$emit('updateModel', { model, field });
    },
    onBlur (value, field) {
      this.$emit('onBlur', { field, value })
    },
    uploadComplete() {
      this.$emit('uploadComplete')
    },

    updateQuerySyntaxValid(isValid) {
      this.field.syntaxValid = isValid;
    },

    removeCustomCompareDateRange(field) {
      if([FIELD_TYPE.DATE_RANGE]?.indexOf(field?.type) <= -1) return;
      if(field.value[field.name]) field.value[field.name] = null;
      if(this.model[field.name]) this.model[field.name][field.name] = null;
    }
  },
  data () {
    return {
      FIELD_TYPE: FIELD_TYPE,
      VALIDATIONS: VALIDATIONS,
      DAYS_OF_WEEK: DAYS_OF_WEEK,
      initDateFormat: 'YYYY-MM-DD'
    };
  },
  mixins: [propsForMixin]
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss" scoped>
  .label-right {
    display: flex;
    flex-direction: row-reverse;
    justify-content: flex-end;

    .app-input {
      margin-right: 10px;
    }
  }
  div input[disabled] {
    border-color: #E5E7EB;
    background-color: #F3F4F6;
    color: #9CA3AF
  }

  .label-left {
    display: flex;

    label {
      margin-right: 10px;
    }
  }
</style>